import React, { useEffect, useState } from 'react';
import { Lightbulb } from 'lucide-react';

interface HomeTip {
  title: string;
  /** PC 문구(우클릭·왼쪽 아래 내 사진) */
  body: string;
  /** 휴대폰 배치 문구(길게 누르기·설정 탭). 없으면 body를 그대로 쓴다 */
  mobileBody?: string;
  /** 휴대폰 배치에서는 보여 주지 않는다 */
  pcOnly?: boolean;
  helpId: string;
}

/**
 * 홈의 '알고 계셨나요?' 팁. 문구가 PC용(body)과 휴대폰 배치용(mobileBody) 두 벌이다.
 * helpId는 HELP_SECTIONS의 장 id이고, 그 장 본문에 실제로 같은 설명이 있어야 한다.
 * 기능 문구를 바꾸면 도움말과 함께 고칠 것(도움말이 먼저 바뀌면 팁이 낡은 안내를 한다).
 */
export const HOME_TIPS: HomeTip[] = [
  {
    title: '대화에서 바로 일정을 만들 수 있습니다',
    body: "메시지를 우클릭해 '일정 만들기'를 고르면 내용이 채워진 일정이 열리고, 대화방 사람들이 참석자로 들어갑니다. 저장하면 대화방에 일정 카드로 공유됩니다.",
    mobileBody: "메시지를 길게 눌러 '일정 만들기'를 고르면 내용이 채워진 일정이 열리고, 대화방 사람들이 참석자로 들어갑니다. 저장하면 대화방에 일정 카드로 공유됩니다.",
    helpId: 'calendar',
  },
  {
    title: '엑셀 표를 그대로 붙여넣을 수 있습니다',
    body: '엑셀·구글 시트에서 셀을 복사해 입력창에 붙여넣으면 사진이 아니라 표로 들어갑니다. 받은 표를 우클릭해 복사하면 엑셀에 표 그대로 다시 붙일 수 있습니다.',
    pcOnly: true,
    helpId: 'messaging',
  },
  {
    title: '원하는 시각에 메시지를 보낼 수 있습니다',
    body: "입력창 왼쪽 클립 메뉴나 전송 버튼 우클릭으로 '예약 전송'을 열면 사진·파일까지 담아 예약할 수 있습니다. 예약한 메시지는 입력창 위 '예약 메시지'에서 고치거나 취소합니다.",
    mobileBody: "입력창 왼쪽 클립 메뉴나 전송 버튼을 길게 눌러 '예약 전송'을 열면 사진·파일까지 담아 예약할 수 있습니다. 예약한 메시지는 입력창 위 '예약 메시지'에서 고치거나 취소합니다.",
    helpId: 'messaging',
  },
  {
    title: '자주 쓰는 대화방은 즐겨찾기에 올려 두세요',
    body: "홈 즐겨찾기의 '추가'를 누르면 대화방을 골라 바로 올릴 수 있고, 동료 이름으로도 찾을 수 있습니다. 대화방 목록에서 방을 우클릭해 '채팅방 고정'을 골라도 됩니다.",
    mobileBody: "홈 즐겨찾기의 '추가'를 누르면 대화방을 골라 바로 올릴 수 있고, 동료 이름으로도 찾을 수 있습니다. 대화방 목록에서 방을 길게 눌러 '채팅방 고정'을 골라도 됩니다.",
    helpId: 'rooms',
  },
  {
    title: '자리를 비울 땐 상태를 바꿔 두세요',
    body: "왼쪽 아래 내 사진을 누르면 '자리 비움'·'다른 용무 중'을 고를 수 있습니다. 5분 동안 PC를 쓰지 않거나 화면을 잠그면 자동으로 자리 비움이 됩니다.",
    mobileBody: "화면 아래 설정 탭의 '내 상태'에서 '자리 비움'·'다른 용무 중'을 고를 수 있습니다. 동료들에게 보이는 내 상태 점 색이 함께 바뀝니다.",
    helpId: 'settings',
  },
  {
    title: '지난 대화는 날짜로 찾아가세요',
    body: '대화방에서 Ctrl + F로 검색창을 연 뒤 달력에서 날짜를 고르면 그날의 첫 대화로 바로 이동합니다.',
    mobileBody: '대화방 오른쪽 위 돋보기 버튼으로 검색창을 연 뒤 달력에서 날짜를 고르면 그날의 첫 대화로 바로 이동합니다.',
    helpId: 'messaging',
  },
  {
    title: '받은 사진을 바로 인쇄할 수 있습니다',
    body: "대화 속 사진을 우클릭해 '인쇄'를 고르면 내려받지 않고 바로 프린터로 보낼 수 있습니다.",
    mobileBody: "대화 속 사진을 길게 눌러 '인쇄'를 고르면 내려받지 않고 바로 프린터로 보낼 수 있습니다.",
    helpId: 'files',
  },
  {
    title: '사진·파일은 서랍에 모여 있습니다',
    body: '대화방 오른쪽 위 폴더 버튼을 누르면 그 방에서 주고받은 사진과 파일만 모아 볼 수 있습니다.',
    mobileBody: "대화방 오른쪽 위 메뉴(≡)에서 '사진·파일'을 누르면 그 방에서 주고받은 사진과 파일만 모아 볼 수 있습니다.",
    helpId: 'files',
  },
  {
    title: '동료의 사진을 누르면 프로필이 열립니다',
    body: '소속과 메일 주소를 확인하고, 메일 주소를 복사하거나 바로 메일을 쓸 수 있습니다. 프로필의 1:1 대화 버튼으로 대화도 시작합니다.',
    helpId: 'rooms',
  },
  {
    title: '알림에 보이는 내용을 정할 수 있습니다',
    body: "내 사진 → '프로필 설정'의 알림 항목에서 알림 팝업에 전체 표시·보낸 사람만·전부 숨김 중 하나를 고릅니다.",
    mobileBody: "설정 탭의 '알림에 표시할 내용'에서 알림에 전체 표시·보낸 사람만·전부 숨김 중 하나를 고릅니다.",
    helpId: 'notifications',
  },
];

/** 휴대폰 배치용 목록 — PC 전용 팁을 빼고 휴대폰 문구로 바꾼다 */
const MOBILE_TIPS: HomeTip[] = HOME_TIPS
  .filter(t => !t.pcOnly)
  .map(t => ({ ...t, body: t.mobileBody ?? t.body }));

const STORAGE_KEY = 'homeTipIndex';

/** 저장 위치는 PC·휴대폰 목록이 공유한다 — 길이가 달라도 나머지 연산으로 접는다 */
function readIndex(length: number): number {
  try {
    const n = Number(localStorage.getItem(STORAGE_KEY));
    return Number.isInteger(n) && n >= 0 ? n % length : 0;
  } catch {
    return 0;
  }
}

function writeIndex(n: number, length: number) {
  try {
    localStorage.setItem(STORAGE_KEY, String(n % length));
  } catch {
    // 저장이 막힌 환경(사생활 보호 모드 등)이면 매번 첫 팁부터 — 기능에는 지장 없다
  }
}

interface HomeTipCardProps {
  /** 해당 도움말 장을 연다 */
  onOpenHelp?: (sectionId: string) => void;
  /**
   * 'mobile' — 웹/PWA 모바일 배치(하단 탭바). 입력 장치(pointer)가 아니라 배치로 고른다:
   * '설정 탭'처럼 화면 구조를 가리키는 문구가 있어 iPad(터치지만 rail 배치)에는 PC 문구가 맞고,
   * 좁은 PC앱은 한 줄 배치여도 PC 문구다.
   */
  variant?: 'pc' | 'mobile';
}

/** 홈에 들어올 때마다 다음 팁을 보여 준다. 다음 번 시작 위치를 마운트 때 미리 저장한다(StrictMode 이중 실행에도 같은 값). */
export const HomeTipCard: React.FC<HomeTipCardProps> = React.memo(({ onOpenHelp, variant = 'pc' }) => {
  const tips = variant === 'mobile' ? MOBILE_TIPS : HOME_TIPS;
  const [index, setIndex] = useState(() => readIndex(tips.length));

  useEffect(() => {
    writeIndex(index + 1, tips.length);
  }, [index, tips.length]);

  // variant가 바뀌어 목록이 짧아져도 범위를 넘지 않게
  const tip = tips[index % tips.length];

  return (
    <section className="bg-white rounded-2xl shadow-sm border border-slate-100 px-[18px] py-3.5" aria-live="polite">
      <div className="flex items-center justify-between mb-2.5">
        <h2 className="flex items-center gap-1.5 text-sm font-bold text-slate-600">
          <Lightbulb size={15} aria-hidden="true" />
          알고 계셨나요?
        </h2>
        <span className="text-xs text-slate-500">{(index % tips.length) + 1} / {tips.length}</span>
      </div>
      <h3 className="text-[15px] font-bold text-slate-800">{tip.title}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{tip.body}</p>
      <div className="mt-3 flex items-center justify-between gap-2">
        {onOpenHelp ? (
          <button
            type="button"
            onClick={() => onOpenHelp(tip.helpId)}
            className="text-[13px] font-semibold text-slate-700 underline underline-offset-[3px] decoration-slate-300 hover:text-slate-900 hover:decoration-slate-500"
          >
            사용설명서에서 보기
          </button>
        ) : <span />}
        <button
          type="button"
          onClick={() => setIndex(i => (i + 1) % tips.length)}
          className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-[13px] font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors"
        >
          다음 팁
        </button>
      </div>
    </section>
  );
});
HomeTipCard.displayName = 'HomeTipCard';
